import { Check, X } from "lucide-react";
import { cx } from "../../lib/cx";

/** Cobalt trade label ("Trucking & Hauling"). */
export function TradeTag({ children, className }) {
  return (
    <span
      className={cx(
        "tag inline-block rounded-md bg-blue-deep px-3 py-1.5 font-semibold tracking-[0.12em] text-white",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Golden Sun badge naming what got built. */
export function BuildBadge({ children, className }) {
  return (
    <div
      className={cx(
        "font-display rounded-lg bg-yellow px-4.5 py-2.5 text-[15px] tracking-[0.06em] text-ink uppercase",
        className,
      )}
    >
      <span className="block text-[10.5px] font-semibold tracking-[0.16em] text-ink/75">
        What we built
      </span>
      {children}
    </div>
  );
}

/** Cobalt chips, one per module of the build. */
export function Modules({ items, className }) {
  return (
    <ul className={cx("flex flex-wrap gap-2", className)}>
      {items.map((item) => (
        <li
          key={item}
          className="rounded-md bg-blue-soft px-3 py-1.75 text-[13.5px] leading-tight font-bold text-[#245D7A]"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

const marks = {
  pain: { Icon: X, color: "text-[#B0533B]", label: "Problem:" },
  now: { Icon: Check, color: "text-[#4E7A3A]", label: "Now:" },
};

/** Headache (✕) or running-now (✓) bullets. */
export function Checklist({ items, kind }) {
  const { Icon, color, label } = marks[kind];
  return (
    <ul>
      {items.map((item) => (
        <li
          key={item}
          className="relative py-2 pl-7 text-[15.5px] leading-normal text-ink"
        >
          <Icon
            size={16}
            strokeWidth={3.5}
            aria-hidden="true"
            className={cx("absolute top-3.25 left-0", color)}
          />
          <span className="sr-only">{label} </span>
          {item}
        </li>
      ))}
    </ul>
  );
}

/** Graphite band of yellow figures with a Golden Sun top rule. */
export function StatBand({ stats, compact = false, className }) {
  return (
    <dl
      className={cx(
        // The 1px gaps show the lighter ground as dividers between figures.
        "flex flex-wrap gap-px border-t-[3px] border-yellow bg-ink-3 text-white",
        className,
      )}
    >
      {stats.map(({ value, label }) => (
        <div
          key={label}
          className={cx(
            "flex flex-col-reverse bg-ink py-5 text-center",
            compact ? "flex-[1_1_110px] px-3" : "flex-[1_1_140px] px-4",
          )}
        >
          <dt className="mt-1 text-[13px] leading-snug text-white/75">
            {label}
          </dt>
          <dd
            className={cx(
              "font-display leading-[1.1] whitespace-nowrap text-yellow",
              compact ? "text-[26px]" : "text-[28px] md:text-[32px]",
            )}
          >
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Pull quote with a yellow rule and an Oswald attribution. */
export function CaseQuote({ quote, large = false, className }) {
  return (
    <figure className={cx("border-l-4 border-yellow pl-5 md:pl-6", className)}>
      <blockquote
        className={cx(
          "text-ink",
          large
            ? "text-[19px] leading-[1.65] md:text-[22px]"
            : "text-[17px] leading-[1.7] md:text-[17.5px]",
        )}
      >
        <p>“{quote.text}”</p>
      </blockquote>
      <figcaption className="mt-4 before:mb-2.5 before:block before:h-0.75 before:w-6.5 before:bg-yellow">
        <span className="font-display block text-[17px] font-semibold tracking-[0.05em] text-ink uppercase">
          {quote.name}
        </span>
        <span className="mt-0.5 block text-sm text-muted">{quote.role}</span>
      </figcaption>
    </figure>
  );
}
